import React from 'react';
import { Check } from 'lucide-react';
import type { EvidenceDecisionStage } from '../../../lib/coverage/stageEvidenceFields';

export interface EvidenceStageSelectorProps {
  value: EvidenceDecisionStage;
  onChange: (stage: EvidenceDecisionStage) => void;
  disabled?: boolean;
  className?: string;
}

const STAGES = [
  { value: 1, label: 'Stage 1 · Screening' },
  { value: 2, label: 'Stage 2 · Engineering validation' },
] as const;

/** A review-view selector; the host retains the site's actual decision stage. */
export function EvidenceStageSelector({
  value,
  onChange,
  disabled = false,
  className,
}: EvidenceStageSelectorProps) {
  return (
    <div className={['space-y-2', className].filter(Boolean).join(' ')} data-testid="coverage-stage-focus">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Evidence review stage">
        {STAGES.map(stage => {
          const selected = value === stage.value;
          return (
            <button
              key={stage.value}
              type="button"
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => { if (!selected) onChange(stage.value); }}
              className={[
                'inline-flex min-h-11 items-center gap-2 rounded-md border px-3 py-2 text-left type-caption',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
                'disabled:cursor-not-allowed disabled:opacity-50',
                selected
                  ? 'border-primary bg-primary font-semibold text-primary-foreground'
                  : 'border-border bg-surface-1 text-text-secondary hover:border-border-strong hover:bg-state-hover',
              ].join(' ')}
              data-testid={`coverage-review-stage-${stage.value}`}
            >
              <span className="inline-flex size-4 shrink-0 items-center justify-center" aria-hidden="true">
                {selected && <Check className="size-4" strokeWidth={3} />}
              </span>
              <span>{stage.label}</span>
            </button>
          );
        })}
      </div>
      <p className="type-caption font-medium text-text-primary" role="status" aria-live="polite">
        Viewing {value === 1 ? STAGES[0].label : STAGES[1].label}
      </p>
      <p className="max-w-[72ch] type-caption text-text-muted">
        {value === 1 ? 'Review land, water, gas access, hazards and project context for conditional screening.' : 'Review engineering, site-specific capacity and formal validation evidence.'}
        {' '}This review view does not change the site's decision stage or report holds.
      </p>
    </div>
  );
}
